import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Row, Col, Button, CustomInput } from 'reactstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import PageHeader from '~/components/Common/PageHeader';
import Loading from '~/components/Loading';
import DeleteModal from '~/components/Modals/DeleteModal';

import api from '~/services/api';
import PartnerWebhookCard from './PartnerWebhookCard';
import ReactivateWebhookModal from './ReactivateWebhookModal';

const ENABLED_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'true', label: 'Ativos' },
  { value: 'false', label: 'Desativados' },
];

const PARTNER_WEBHOOKS_URL = 'administrator/partner-webhooks';
const PARTNER_APPS_URL = 'administrator/partner-applications';

const PartnerWebhooks = () => {
  const location = useLocation();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  // Filtros
  const [appOptions, setAppOptions] = useState([]);
  const [appFilter, setAppFilter] = useState(null);
  const [enabledFilter, setEnabledFilter] = useState(ENABLED_OPTIONS[0]);
  const [autoDisabledOnly, setAutoDisabledOnly] = useState(false);

  // Bandeira pra evitar fetch duplo durante inicialização do deeplink
  const [initialized, setInitialized] = useState(false);

  // Modais
  const [showReactivate, setShowReactivate] = useState(false);
  const [webhookToReactivate, setWebhookToReactivate] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [webhookToDelete, setWebhookToDelete] = useState(null);

  const buildParams = useCallback(
    (cursor = null) => {
      const params = { limit: 20 };
      if (cursor) params.cursor = cursor;
      if (appFilter?.value) params.partner_application_id = appFilter.value;
      if (enabledFilter?.value !== '') params.enabled = enabledFilter.value;
      if (autoDisabledOnly) params.auto_disabled_only = true;
      return params;
    },
    [appFilter, enabledFilter, autoDisabledOnly]
  );

  const formatItem = w => ({
    ...w,
    formatted_created_at: w.created_at
      ? format(parseISO(w.created_at), "dd/MMM/yy - HH'h'mm", { locale: ptBR })
      : '-',
    formatted_last_success_at: w.last_success_at
      ? format(parseISO(w.last_success_at), "dd/MMM/yy - HH'h'mm", {
          locale: ptBR,
        })
      : null,
    formatted_last_failure_at: w.last_failure_at
      ? format(parseISO(w.last_failure_at), "dd/MMM/yy - HH'h'mm", {
          locale: ptBR,
        })
      : null,
  });

  const fetchList = useCallback(
    async (reset = true) => {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      try {
        const params = buildParams(reset ? null : nextCursor);
        const { data } = await api.get(PARTNER_WEBHOOKS_URL, { params });
        const formatted = (data.data || []).map(formatItem);
        if (reset) {
          setItems(formatted);
        } else {
          setItems(prev => [...prev, ...formatted]);
        }
        setNextCursor(data.pagination?.next_cursor || null);
        setHasMore(Boolean(data.pagination?.has_more));
      } catch (err) {
        toast.error(
          err?.response?.data?.message || 'Erro ao carregar webhooks parceiros.'
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [buildParams, nextCursor]
  );

  // Carrega lista quando filtros mudam (reseta cursor) — só depois do init
  useEffect(() => {
    if (!initialized) return;
    fetchList(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, appFilter, enabledFilter, autoDisabledOnly]);

  // Carrega aplicações para o dropdown de filtro + processa deeplink (1x no mount)
  useEffect(() => {
    document.title = 'Webhooks Parceiros | Viddy';

    const queryParams = new URLSearchParams(location.search);
    const queryAppId = queryParams.get('partner_application_id');

    const initialize = async () => {
      let apps = [];
      try {
        const { data } = await api.get(PARTNER_APPS_URL, {
          params: { limit: 100 },
        });
        apps = (data.data || []).map(app => ({
          value: app.id,
          label: `${app.name} (${
            app.environment === 'live' ? 'PROD' : 'TESTE'
          })`,
        }));
      } catch (err) {
        // silencioso — filtro fica apenas com a opção "todas"
      }
      setAppOptions([{ value: null, label: 'Todas as aplicações' }, ...apps]);

      // Deeplink: pré-popula filtro de aplicação se vier na URL
      if (queryAppId) {
        const appOpt = apps.find(a => a.value === queryAppId);
        if (appOpt) {
          setAppFilter(appOpt);
        }
      }

      setInitialized(true);
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteURL = useMemo(() => {
    if (!webhookToDelete?.id) return '';
    return `${PARTNER_WEBHOOKS_URL}/${webhookToDelete.id}`;
  }, [webhookToDelete]);

  // DeleteModal espera setData(data) com formato { ... }. Como nosso shape é
  // diferente, passamos um setData wrapper que apenas reseta a lista após delete.
  const handleAfterDelete = () => {
    fetchList(true);
  };

  return (
    <div className="page-content">
      <Container className="container new-layout wide">
        <PageHeader
          pageTitle="WEBHOOKS PARCEIROS"
          responsivePosition="start"
          desktopPosition="start"
        />

        <Row className="w-100 mx-0 mb-4 d-flex flex-row align-items-center">
          <Col className="px-0 d-flex flex-row align-items-baseline flex-wrap">
            <span className="text-left mt-4 mt-lg-0 d-inline text-muted font-size-14 mr-3">
              Filtros
            </span>

            <div
              style={{ minWidth: 260, marginRight: 8 }}
              className="mb-2 mb-lg-0"
            >
              <Select
                value={appFilter || appOptions[0] || null}
                onChange={opt => setAppFilter(opt?.value ? opt : null)}
                options={appOptions}
                placeholder="Aplicação"
                isSearchable
                classNamePrefix="select"
              />
            </div>

            <div
              style={{ minWidth: 165, marginRight: 8 }}
              className="mb-2 mb-lg-0"
            >
              <Select
                value={enabledFilter}
                onChange={setEnabledFilter}
                options={ENABLED_OPTIONS}
                placeholder="Status"
                classNamePrefix="select"
              />
            </div>

            <div className="d-flex align-items-center mb-2 mb-lg-0">
              <CustomInput
                type="switch"
                id="auto-disabled-switch"
                name="auto-disabled-switch"
                label="Apenas auto-desativados"
                checked={autoDisabledOnly}
                onChange={() => setAutoDisabledOnly(prev => !prev)}
                className="text-muted"
              />
            </div>
          </Col>
        </Row>

        {loading ? (
          <Loading />
        ) : (
          <>
            {items.length > 0 ? (
              <>
                <Row className="w-100 mx-0 mb-4 d-none d-lg-flex px-3">
                  <Col xs={3} align="start">
                    <div className="new-layout-table-column-label">
                      Aplicação
                    </div>
                  </Col>
                  <Col xs={3} align="start">
                    <div className="new-layout-table-column-label">URL</div>
                  </Col>
                  <Col xs={2} align="start">
                    <div className="new-layout-table-column-label">Eventos</div>
                  </Col>
                  <Col xs={1} align="start">
                    <div className="new-layout-table-column-label">Status</div>
                  </Col>
                  <Col xs={1} align="start">
                    <div className="new-layout-table-column-label">Falhas</div>
                  </Col>
                  <Col xs={1} align="start">
                    <div className="new-layout-table-column-label">Criado</div>
                  </Col>
                  <Col xs={1} align="center">
                    <div className="new-layout-table-column-label">Ações</div>
                  </Col>
                </Row>

                {items.map(webhook => (
                  <Row className="w-100 mx-0" key={webhook.id}>
                    <Col className="px-0" xs={12}>
                      <PartnerWebhookCard
                        webhook={webhook}
                        onReactivate={w => {
                          setWebhookToReactivate(w);
                          setShowReactivate(true);
                        }}
                        onDelete={w => {
                          setWebhookToDelete(w);
                          setShowDelete(true);
                        }}
                      />
                    </Col>
                  </Row>
                ))}

                {hasMore && (
                  <Row className="w-100 mx-0 mb-4">
                    <Col className="px-0 d-flex justify-content-center">
                      <Button
                        color="primary"
                        outline
                        size="lg"
                        disabled={loadingMore}
                        onClick={() => fetchList(false)}
                      >
                        {loadingMore ? 'Carregando...' : 'Carregar mais'}
                      </Button>
                    </Col>
                  </Row>
                )}
              </>
            ) : (
              <h3>Nenhum webhook parceiro encontrado.</h3>
            )}
          </>
        )}
      </Container>

      <ReactivateWebhookModal
        show={showReactivate}
        setShow={setShowReactivate}
        webhook={webhookToReactivate}
        onReactivated={updated => {
          if (updated) {
            setItems(prev =>
              prev.map(w => (w.id === updated.id ? formatItem(updated) : w))
            );
          }
        }}
      />

      <DeleteModal
        show={showDelete}
        setShow={setShowDelete}
        deleteUrl={deleteURL}
        getUrl={PARTNER_WEBHOOKS_URL}
        params={buildParams()}
        setData={handleAfterDelete}
        itemsArray={items}
        setItemToBeDeleted={setWebhookToDelete}
        headerText="Deseja mesmo excluir o webhook?"
        bodyText="Essa ação irá remover o webhook permanentemente. O parceiro precisará recriá-lo via API."
        itemName={webhookToDelete?.description || webhookToDelete?.url}
        successMsg="Webhook excluído com sucesso!"
        btnText="Excluir"
      />
    </div>
  );
};

export default PartnerWebhooks;
